import ArrowRightAltOutlinedIcon from '@mui/icons-material/ArrowRightAltOutlined';
import { Button } from '@mui/material';
import { useAtomValue } from 'jotai';
import { cloneDeep } from 'lodash-es';
import { Dispatch, FC, HTMLAttributes, SetStateAction, useCallback, useMemo } from 'react';
import { IDealDataModel } from '../../../../data-models/deal.data-model';
import { useDoUpdateStage } from '../../hooks/useDealActions';
import { dealBoardSettingsState, selectedDealState } from '../../state/DealboardUIStateJ';
import { getCurrentDealPosition } from './dealNavigationUtils';

interface NextStageButtonProps extends HTMLAttributes<HTMLButtonElement> {
  setShowLoader: Dispatch<SetStateAction<boolean>>;
  setAlert: Dispatch<SetStateAction<{ message: string; severity: 'error' | 'success' } | undefined>>;
}

export const NextStageButton: FC<NextStageButtonProps> = ({ setShowLoader, setAlert }) => {
  const selectedDeal = useAtomValue(selectedDealState);
  const dealBoardSettings = useAtomValue(dealBoardSettingsState);
  const doUpdateStage = useDoUpdateStage();

  const nextStageId = useMemo(() => {
    if (!selectedDeal || !dealBoardSettings) {
      return null;
    }
    const { columnIndex } = getCurrentDealPosition(dealBoardSettings, selectedDeal);
    if (columnIndex === dealBoardSettings.length - 1) {
      return null;
    } else {
      return dealBoardSettings[columnIndex + 1].stage.id;
    }
  }, [selectedDeal, dealBoardSettings]);

  const handleMoveToNextStage = useCallback(async () => {
    if (!selectedDeal || !dealBoardSettings || !nextStageId) {
      return;
    }
    setShowLoader(true);

    const updatedDeal: IDealDataModel = {
      ...cloneDeep(selectedDeal),
      stageId: nextStageId,
      stageUpdateDate: new Date().toISOString(),
    };

    await doUpdateStage(selectedDeal, updatedDeal, {
      onError: () => setAlert({ message: 'Error moving to next stage', severity: 'error' }),
      onComplete: () => {
        setShowLoader(false);
      },
    });
  }, [selectedDeal, dealBoardSettings, nextStageId, setShowLoader, doUpdateStage, setAlert]);

  return (
    <Button
      startIcon={<ArrowRightAltOutlinedIcon />}
      variant={'contained'}
      color={'secondary'}
      size={'medium'}
      disabled={!nextStageId}
      onClick={handleMoveToNextStage}
    >
      Move to next stage
    </Button>
  );
};
